import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import '@/App.css';
import * as serviceWorkerRegistration from './utils/serviceWorkerRegistration';

// Apply theme on startup before render
(() => {
  const theme = localStorage.getItem('theme');
  if (theme === 'dark') document.documentElement.classList.add('dark');
})();
import { BackgroundTaskProvider } from './context/BackgroundTaskContext';
import OfflineIndicator from './components/OfflineIndicator';
import TutorialWalkthrough, { isTourCompleted } from './components/TutorialWalkthrough';

// Scroll to top on every route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Tutorial wrapper - only show for authenticated users
function TutorialGuard() {
  const { user } = useAuth();
  const [forceOpen, setForceOpen] = useState(false);

  useEffect(() => {
    const handler = () => setForceOpen(true);
    window.addEventListener('start-tutorial', handler);
    return () => window.removeEventListener('start-tutorial', handler);
  }, []);

  if (!user) return null;
  return (
    <TutorialWalkthrough
      forceOpen={forceOpen}
      onClose={() => setForceOpen(false)}
    />
  );
}

// Eagerly loaded (landing + auth)
import LandingPage from './pages/LandingPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';

// Lazy-loaded pages (code-split)
const LawLibraryPage = lazy(() => import('./pages/LawLibraryPage'));
const LawSearchPage = lazy(() => import('./pages/LawSearchPage'));
const ProvisionSearchPage = lazy(() => import('./pages/ProvisionSearchPage'));
const LawContentPage = lazy(() => import('./pages/LawContentPage'));
const ArticleSectionsPage = lazy(() => import('./pages/ArticleSectionsPage'));
const SectionContentPage = lazy(() => import('./pages/SectionContentPage'));
const ReferenceDetailPage = lazy(() => import('./pages/ReferenceDetailPage'));
const ArticleDetailPage = lazy(() => import('./pages/ArticleDetailPage'));
const SectionDetailPage = lazy(() => import('./pages/SectionDetailPage'));
const ProvisionDetailPage = lazy(() => import('./pages/ProvisionDetailPage'));
const WorkspacePage = lazy(() => import('./pages/WorkspacePage'));
const SynthesisPage = lazy(() => import('./pages/SynthesisPage'));
const SharePage = lazy(() => import('./pages/SharePage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const CategoryLawsPage = lazy(() => import('./pages/CategoryLawsPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const RefundPolicyPage = lazy(() => import('./pages/RefundPolicyPage'));
const ChangePasswordPage = lazy(() => import('./pages/ChangePasswordPage'));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'));
const JurisprudencePage = lazy(() => import('./pages/JurisprudencePage'));
const BarQAPage = lazy(() => import('./pages/BarQAPage'));
const ZapToolsPage = lazy(() => import('./pages/ZapToolsPage'));
const MyLawsCardPage = lazy(() => import('./pages/MyLawsCardPage'));

// Context
import { AuthProvider, useAuth } from './context/AuthContext';

// Components
import AuthenticatedLayout from './components/AuthenticatedLayout';

const BACKEND_URL = (() => {
  const envUrl = process.env.REACT_APP_BACKEND_URL || '';
  // Use relative path if env URL host doesn't match current host (deployment scenario)
  if (envUrl && typeof window !== 'undefined' && window.location) {
    try {
      const envHost = new URL(envUrl).host;
      if (envHost !== window.location.host) return '';
    } catch { return ''; }
  }
  return envUrl;
})();
const API = `${BACKEND_URL}/api`;

export { BACKEND_URL };

export const api = axios.create({
  baseURL: API,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const message = error.response?.data?.detail || '';
      const isTokenExpired = message.toLowerCase().includes('expired');
      const isInvalidToken = message.toLowerCase().includes('invalid token');
      // Only auto-logout on genuine auth failures (expired/invalid token)
      // Don't logout on transient errors like upload timeouts
      if (isTokenExpired || isInvalidToken) {
        localStorage.removeItem('token');
        if (window.location.pathname !== '/') {
          window.location.href = '/';
        }
      }
    } else if (error.response?.status === 403) {
      const message = error.response?.data?.detail || 'Access denied';
      const requestUrl = error.config?.url || '';
      // Don't redirect on auth/me or subscription-related endpoints
      const isAuthCheck = requestUrl.includes('/auth/me') || requestUrl.includes('/auth/subscription') || requestUrl.includes('/payments/');
      if (isAuthCheck) {
        // Let the caller handle it
        return Promise.reject(error);
      }
      const isSubscriptionIssue = message.toLowerCase().includes('trial') || 
                                  message.toLowerCase().includes('subscription') ||
                                  message.toLowerCase().includes('expired') ||
                                  message.toLowerCase().includes('pending');
      
      if (isSubscriptionIssue && window.location.pathname !== '/subscribe') {
        window.location.href = '/subscribe';
      } else if (!isSubscriptionIssue) {
        toast.error(message, { duration: 5000 });
      }
    }
    return Promise.reject(error);
  }
);

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">
      <div className="text-purple-600">Loading...</div>
    </div>;
  }

  // If a token exists but user load failed (likely transient network/502 during
  // post-deploy cold start), show a "Reconnecting…" state instead of bouncing
  // the user to the landing page. AuthContext retries on the next navigation.
  if (!user && typeof window !== 'undefined' && localStorage.getItem('token')) {
    return <div className="min-h-screen flex items-center justify-center flex-col gap-3" data-testid="auth-reconnecting">
      <div className="text-purple-600 animate-pulse">Reconnecting…</div>
      <button
        onClick={() => window.location.reload()}
        className="text-xs text-gray-500 hover:text-gray-700 underline"
        data-testid="auth-reconnecting-retry-btn"
      >
        Retry
      </button>
    </div>;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return <AuthenticatedLayout>{children}</AuthenticatedLayout>;
}

function App() {
  // Register service worker for offline support
  useEffect(() => {
    serviceWorkerRegistration.register();
  }, []);

  const PageLoader = () => (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-purple-600 animate-pulse">Loading...</div>
    </div>
  );

  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <BackgroundTaskProvider>
        <div className="App">
          <OfflineIndicator />
          <TutorialGuard />
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LandingPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route
              path="/library"
              element={
                <PrivateRoute>
                  <LawLibraryPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/search"
              element={
                <PrivateRoute>
                  <LawSearchPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/provision-search"
              element={
                <PrivateRoute>
                  <ProvisionSearchPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/laws/:lawId"
              element={
                <PrivateRoute>
                  <LawContentPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/laws/:lawId/article/:articleNumber"
              element={
                <PrivateRoute>
                  <ArticleSectionsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/laws/:lawId/section/:sectionId"
              element={
                <PrivateRoute>
                  <SectionContentPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/references/:refId"
              element={
                <PrivateRoute>
                  <ReferenceDetailPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/articles/:articleId"
              element={
                <PrivateRoute>
                  <ArticleDetailPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/sections/:sectionId"
              element={
                <PrivateRoute>
                  <SectionDetailPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/provisions/:provisionId"
              element={
                <PrivateRoute>
                  <ProvisionDetailPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/workspace"
              element={
                <PrivateRoute>
                  <WorkspacePage />
                </PrivateRoute>
              }
            />
            <Route
              path="/synthesis"
              element={
                <PrivateRoute>
                  <SynthesisPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/jurisprudence"
              element={
                <PrivateRoute>
                  <JurisprudencePage />
                </PrivateRoute>
              }
            />
            <Route
              path="/bar-qa"
              element={
                <PrivateRoute>
                  <BarQAPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/zap-tools"
              element={
                <PrivateRoute>
                  <ZapToolsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/my-laws/:cardId"
              element={
                <PrivateRoute>
                  <MyLawsCardPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/share"
              element={
                <PrivateRoute>
                  <SharePage />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <SettingsPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/change-password"
              element={
                <PrivateRoute>
                  <ChangePasswordPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <PrivateRoute>
                  <AdminDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/admin/category/:categoryId"
              element={
                <PrivateRoute>
                  <CategoryLawsPage />
                </PrivateRoute>
              }
            />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route 
              path="/subscribe" 
              element={<SubscriptionPage />} 
            />
          </Routes>
          </Suspense>
          <Toaster position="top-center" />
        </div>
        </BackgroundTaskProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
